import React from 'react';
import camelcase from 'camelcase-es5';

import InputLabel from '../InputLabel';

import TextFieldFormControl from './TextFieldFormControl';
import TextFieldInput from './TextFieldInput';
import TextFieldFooter from './TextFieldFooter';
import TextFieldBase from './TextFieldBase';
import TextFieldSuffix from './TextFieldSuffix';
import useFormControlReducer from '../../hooks/useFormControlReducer';
import TSuffixedTextField from './@types/SuffixedTextField';
import generateTextFieldIds, { isCharsExceeded } from './generateTextFieldIds';

const SuffixedTextField = ({
  errorText,
  helperText,
  labelText = '',
  optionalText,
  suffixText,
  charsRemainingText,
  charsExceededText,
  value,
  inputRef,
  isRequired,
  autoFocus,
  onChange,
  onFocus,
  onBlur,
  onMouseOut,
  onMouseOver,
  onKeyPress,
  type = 'text',
  id = `SuffixedTextField-${camelcase(labelText)}`,
  maxLength,
  name,
  inputMode,
  dataTestId,
  placeholder,
}: TSuffixedTextField) => {
  const { state, handlers } = useFormControlReducer({
    onChange,
    onFocus,
    onBlur,
    onMouseOut,
    onMouseOver,
    errorText,
    value,
  });

  const { isDirty, isEmpty, isErrored, isFocused, characterCount } = state;
  const { handleChange, handleFocus, handleBlur, handleMouseOver, handleMouseOut } = handlers;

  const charsExceeded = isCharsExceeded(characterCount, maxLength);
  const { helperTextId, errorTextId, charsExceededId } = generateTextFieldIds(id, Boolean(errorText), charsExceeded);

  return (
    <TextFieldBase kind="suffixed">
      {labelText && (
        <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
      )}
      <TextFieldFormControl isFocused={isFocused} isErrored={isErrored} isDirty={isDirty} isEmpty={isEmpty}>
        <TextFieldInput
          type={type}
          id={id}
          name={name}
          ref={inputRef}
          isRequired={isRequired}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onKeyPress={onKeyPress}
          inputMode={inputMode}
          data-testid={dataTestId}
          placeholder={placeholder}
          helperTextId={helperTextId}
          errorTextId={errorTextId}
          charsExceededId={charsExceededId}
          autoFocus={autoFocus}
        />
        <TextFieldSuffix>{suffixText}</TextFieldSuffix>
      </TextFieldFormControl>
      <TextFieldFooter
        errorText={errorText}
        helperText={helperText}
        charsRemainingText={charsRemainingText}
        charsExceededText={charsExceededText}
        maxLength={maxLength}
        characterCount={characterCount}
        id={helperTextId}
        errorTextId={errorTextId}
        charsExceededId={charsExceededId}
        charsExceeded={charsExceeded}
      />
    </TextFieldBase>
  );
};

export default SuffixedTextField;
